import { createStore } from 'vuex';
import { io } from 'socket.io-client';
import http from '@/utils/http-common';
import router from '@/router';
import Modal from './modules/modal';

// url is gelijk aan de server indien local.
// in geval van vite is het http://127.0.0.1:5173/

/* voorbeeld 
// client-side
import { io } from "socket.io-client";
const socket = io("https://api.example.com", {
  withCredentials: true,
  extraHeaders: {
    "my-custom-header": "abcd"
  }
});
*/

const endpointTemplate = (strings, ...keys) => {
  return (...values) => {
    let dict = values[values.length - 1] || {};
    let result = [strings[0]];
    keys.forEach(function (key, i) {
      let value = Number.isInteger(key) ? values[key] : dict[key];
      result.push(value, strings[i + 1]);
    });
    return result.join('');
  };
};

export default createStore({
  state: {
    connected: false,
    version: null,
    socket: null,
    myroom: 'outside',
    welcomeText:
      'Als je je gekrenkt voelt kun je er hier over praten met een AI-coach. Een coach-sessie duurt max 5 minuten, als het meezit ben je daarna weer gladgestreken. Onze coaches zijn streng, maar zullen je op een respectvolle manier begeleiden.',
    total: 0,
    user: null,
    _typingTimeout: null, // voor de isTyping functie, voorkomt dat er te vaak een bericht wordt gestuurd.
    secondsLeft: -1,
    endTherapy: false,
    therapistImage: null,
    therapistName: '',
    isLoading: false, //is er een spinner
    isLoaded: false, //is store gemount
    models: ['gpt-4o-mini'], //niet bereikbaar:, "gpt-4"],
    messages: {}, //chatId messages
    therapyViewMessages: {}, //therapyId messages voor therapyView beheer
    therapyMessages: [], //therapy messages voor 1 therapy voor de socketversie
    lastMessageByKrenk: 'Welkom, u voelt zich gekrenkt?',
    endPoints: {
      getSession: endpointTemplate`/`,
      getVersion: endpointTemplate`version`,
      getImage: endpointTemplate`image/generate`,
      imagePrompt: endpointTemplate`image/imageprompt/${'image_id'}`,
      imagePrompts: endpointTemplate`image/imageprompts`,
      user: endpointTemplate`user/${'user_id'}`,
      users: endpointTemplate`user`,
      newChat: endpointTemplate`chat/newchat`,
      postChat: endpointTemplate`chat/${'chat_id'}`,
      getChat: endpointTemplate`chat/${'chat_id'}`,
      getChats: endpointTemplate`chat/chats`,
      therapy: endpointTemplate`therapy/${'therapy_id'}`,
      getTherapies: endpointTemplate`therapy/therapies`,
      getTherapyCompletion: endpointTemplate`therapy`,
    },
  },
  getters: {
    messages: (state) => (chat_id) => {
      if (!chat_id || !state.messages[chat_id]) return [];
      return state.messages[chat_id];
    },
    therapyMessages: (state) => {
      return state.therapyMessages;
    },
    therapyViewMessages: (state) => (therapy_id) => {
      if (!therapy_id || !state.therapyViewMessages[therapy_id]) return [];
      return state.therapyViewMessages[therapy_id];
    },
    therapistImage: (state) => {
      return state.therapistImage;
    },
    therapistName: (state) => {
      return state.therapistName;
    },
    secondsLeft: (state) => {
      return state.secondsLeft;
    },
    isLoading: (state) => {
      return state.isLoading;
    },
    isLoaded: (state) => {
      return state.isLoaded;
    },
    debug: (state) => {
      return state.debug;
    },
    user: (state) => {
      if (!state.user) {
        const chatUser = JSON.parse(localStorage.getItem('chat_user'));
        state.user = chatUser;
      }
      return state.user;
    },
    endTherapy: (state) => {
      return state.endTherapy;
    },
    isLoggedIn: (state) => {
      if (!state.user) {
        const chatUser = JSON.parse(localStorage.getItem('chat_user'));
        state.user = chatUser;
      }

      return state.user !== null;
    },
  },
  mutations: {
    disconnectSocket(state) {
      state.socket.disconnect();
    },
    deleteTherapyView(state, therapy_id) {
      if (state.therapyViewMessages[therapy_id]) {
        delete state.therapyViewMessages[therapy_id];
      }
    },
    deleteMessages(state, chat_id) {
      if (state.messages[chat_id]) {
        delete state.messages[chat_id];
      }
    },
    addMessage(state, { chat_id, role, content }) {
      if (!chat_id) return;
      if (state.messages[chat_id] === undefined) {
        state.messages[chat_id] = [];
      }
      state.messages[chat_id].push({ role, content });
    },
    addTherapyViewMessage(state, { therapy_id, role, content }) {
      if (!therapy_id) return;
      if (state.therapyViewMessages[therapy_id] === undefined) {
        state.therapyViewMessages[therapy_id] = [];
      }
      state.therapyViewMessages[therapy_id].push({ role, content });
    },
    setTherapyViewMessages(state, { therapy_id, messages }) {
      state.therapyViewMessages[therapy_id] = messages;
    },
    setMessages(state, { chat_id, messages }) {
      state.messages[chat_id] = messages;
    },
    addTherapyMessage(state, { role, content }) {
      state.therapyMessages.push({ role, content });
      if (role === 'assistant') {
        state.lastMessageByKrenk = content;
      }
    },
    removeTherapyMessage(state) {
      if (state.therapyMessages.length == 0) return;
      if (
        state.therapyMessages[state.therapyMessages.length - 1].role === 'user'
      ) {
        state.therapyMessages[state.therapyMessages.length - 1].role = 'engine';
        state.therapyMessages[state.therapyMessages.length - 1].content =
          '<span>te snel getypt, bericht verwijderd</span>';
      }
    },
    joinRoom(state, room) {
      if (!state.socket.connected) {
        state.socket.connect();
      }
      state.socket.emit('joinRoom', room);
    },
    //verlaat de therapy kamer, timers lopen niet verder, maar de therapie is nog niet afgelopen.
    leaveAllRooms(state) {
      state.socket.emit('leaveAllRooms');
    },
    // doorgeven of client aan het typen is. max 1x per seconde.
    isTyping(state) {
      if (!state.socket) return;
      if (state.myroom !== 'therapyroom') return;
      if (!state._typingTimeout) {
        state.socket.emit('isTyping');
        state._typingTimeout = setTimeout(() => {
          state._typingTimeout = null;
        }, 1000); // Only allow one emission per second
      }
    },
    //doorgeven dat de client niet meer aan het typen is.
    isNotTyping(state) {
      if (!state.socket) return;
      if (state.myroom !== 'therapyroom') return;
      state.socket.emit('isNotTyping');
    },
    setUser(state, user) {
      localStorage.setItem('chat_user', JSON.stringify(user));
      state.user = user;
    },
    setVersion(state, version) {
      state.version = version;
    },
    clearUser(state) {
      localStorage.removeItem('chat_user');
      state.user = null;
    },
    setLoaded(state) {
      state.isLoaded = true;
    },
    showLoading(state, bool) {
      /*
      de timeout op loading is true werkt niet correct bij bijv search.
      de console.log is niet betrouwbaar genoeg om een goede debug te maken.
      */
      if (bool) {
        state.isLoading = true;
      } else {
        state.isLoading = false;
      }
    },
  },
  actions: {
    async mount({ commit, dispatch }) {
      commit('showLoading', true);
      const socket = io(import.meta.env.VITE_SERVER, {
        withCredentials: true,
        path: import.meta.env.VITE_SOCKET,
        transports: ['polling', 'websocket'], //polling moet eerst ivm cookies!!!
      });
      await dispatch('setSocket', socket);
      commit('showLoading', false);
      commit('setLoaded');
    },
    async closeTherapy({ state }) {
      if (!state.socket) return;
      await state.socket.emit('closeTherapy');
    },
    async setSocket({ state, commit, dispatch }, socket) {
      if (state.socket) {
        throw new Error('socket already set');
      }
      state.socket = socket;
      state.socket.on('connect', () => {
        state.connected = true;

        const engine = state.socket.io.engine;
        if (engine.transport.name === 'polling') {
          // Socket.IO connects via polling first and upgrades to WebSocket shortly after.
          // Only show the warning if no upgrade happens within 5 seconds.
          const upgradeTimeout = setTimeout(() => {
            if (engine.transport.name === 'polling') {
              dispatch('modal/setModal', {
                title: 'Browserprobleem',
                text: 'Jouw browser heeft geen stabiele verbinding kunnen maken. Gebruik een andere browser.',
                type: 'blocking',
              });
              state.socket.disconnect();
              state.socket = null;
            }
          }, 5000);
          engine.once('upgrade', () => {
            console.log('socket upgraded');
            clearTimeout(upgradeTimeout);
            //deze staat hier omdat nu de cookies vast wel gesettled zijn.
            http
              .get('version')
              .then((res) => commit('setVersion', res.data.version))
              .catch(() => {});
          });
        }
      });

      state.socket.on('disconnect', () => {
        state.connected = false;
        state.total = -1;
        router.push({ name: 'Home' });
      });

      state.socket.on('waitingRoomSize', (total) => {
        state.total = total;
      });

      state.socket.on('therapyMessage', ({ content, role }) => {
        commit('addTherapyMessage', {
          role,
          content,
        });
      });

      state.socket.on('therapyRoomsFull', () => {
        dispatch('modal/setModal', {
          title: 'Vol!',
          text: `Alle therapiekamers zijn bezet....`,
          type: 'notification',
        });
      });

      state.socket.on('therapistNotAvailable', () => {
        dispatch('modal/setModal', {
          title: 'Je therapeut zit al in een sessie',
          text: `Nog even wachten aub...`,
          type: 'notification',
        });
      });

      state.socket.on('secondsLeft', (secondsLeft) => {
        state.secondsLeft = secondsLeft;
      });

      state.socket.on('recuperEnter', ({ therapyMessages }) => {
        /*therapyMessages.push({
          role: 'secretaresse',
          content:
            'sorry de therapeut moet naar zijn volgende afspraak. je kunt het later nog eens proberen.',
        });*/
        state.myroom = 'recuperroom';
        state.therapyMessages = therapyMessages;
        state.lastMessageByKrenk = null;
        state.secondsLeft = -1;

        router.push({ name: 'RecuperRoom' });
      });
      state.socket.on(
        'therapyEnter',
        async ({
          secondsLeft,
          therapyMessages,
          therapistImage,
          therapistName,
        }) => {
          commit('showLoading', false);

          if (secondsLeft) {
            state.therapyMessages = therapyMessages;

            //find latest message by assistant and set as lastMessageByKrenk
            for (let i = therapyMessages.length - 1; i >= 0; i--) {
              if (therapyMessages[i].role === 'assistant') {
                state.lastMessageByKrenk = therapyMessages[i].content;
                break;
              }
            }
            state.secondsLeft = secondsLeft;
            state.endTherapy = secondsLeft <= 0;
            state.therapistImage = therapistImage;
            state.therapistName = therapistName;
            state.myroom = 'therapyroom';
            router.push({ name: 'TherapyRoom' });
          } else {
            console.log('error creating new therapy, back to waitingroom');
            state.myroom = 'waitingroom';
            router.push({ name: 'WaitingRoom' });
          }
        }
      );
      state.socket.on('waitingRoomEnter', () => {
        state.myroom = 'waitingroom';
        router.push({ name: 'WaitingRoom' });
      });

      state.socket.on('outside', () => {
        // melding maken?
        state.myroom = 'outside';
        router.push({ name: 'Home' });
      });

      state.socket.on('therapyRoomFull', () => {
        // melding maken dat therapy bezet is!
        dispatch('modal/setModal', {
          title: 'Vol!',
          text: `Alle therapiekamers zijn bezet`,
          type: 'notification',
        });
        router.push({ name: 'WaitingRoom' });
      });

      state.socket.on('therapyError', () => {
        commit('showLoading', false);
        state.myroom = 'waitingroom';
        dispatch('modal/setModal', {
          title: 'Technische storing',
          text: 'Er is een fout opgetreden bij het starten van de sessie. Probeer het later opnieuw.',
          type: 'notification',
        });
        router.push({ name: 'WaitingRoom' });
      });
    },
    async get({ commit, state }, { end_point, path_vars = {} }) {
      //chat/completions
      commit('showLoading', true);
      const url = state.endPoints[end_point](path_vars);
      try {
        const res = await http.get(url, {});
        commit('showLoading', false);
        return res.data;
      } catch (error) {
        commit('showLoading', false);
        throw new Error(error);
      }
    },

    async put(
      { state, commit },
      { end_point, path_vars = {}, body_vars = {} }
    ) {
      commit('showLoading', true);
      try {
        const url = state.endPoints[end_point](path_vars);
        const res = await http.put(url, body_vars);
        commit('showLoading', false);
        // expressjs error handler
        return res;
      } catch (error) {
        commit('showLoading', false);
        throw new Error(error);
      }
    },

    async post(
      { state, commit },
      { end_point, path_vars = {}, body_vars = {} }
    ) {
      commit('showLoading', true);
      try {
        const url = state.endPoints[end_point](path_vars);
        const res = await http.post(url, body_vars);
        commit('showLoading', false);
        // expressjs error handler
        return res;
      } catch (error) {
        commit('showLoading', false);
        throw new Error(error);
      }
    },
    async delete({ state, commit }, { end_point, path_vars = {} }) {
      commit('showLoading', true);
      try {
        const url = state.endPoints[end_point](path_vars);
        const res = await http.delete(url, {});

        commit('showLoading', false);
        return res.data;
      } catch (error) {
        commit('showLoading', false);
        throw new Error(error);
      }
    },
    async logOut({ commit }) {
      commit('clearUser');
      try {
        await http.post('auth/logout');
      } catch (error) {
        console.log('error', error);
        throw new Error(error);
      }
    },
    async submitLogin({ commit }, { user_name, password }) {
      try {
        const res = await http.post('auth/login', {
          user_name,
          password,
        });
        commit('setUser', res.data);
      } catch (err) {
        throw new Error(err);
      }
    },
  },
  modules: {
    modal: Modal,
  },
});
